/**
 * FieldFeedbackContext — exposes per-field unresolved/total feedback counts
 * so tree rows and field components can render annotation badges without
 * each one having to query feedback.
 */

import { useFeedbackForList } from '@/features/feedback/feedback.queries';
import type { UUID } from '@certhon/models';
import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface FieldFeedbackCounts {
  total: number;
  unhandled: number;
}

type FieldFeedbackMap = Map<string, FieldFeedbackCounts>;

const FieldFeedbackContext = createContext<FieldFeedbackMap | null>(null);

interface ProviderProps {
  protoListId: UUID;
  children: ReactNode;
}

export function FieldFeedbackProvider({
  protoListId,
  children,
}: ProviderProps) {
  const { data: feedback } = useFeedbackForList(protoListId);

  const map = useMemo<FieldFeedbackMap>(() => {
    const m = new Map<string, FieldFeedbackCounts>();
    if (!feedback) return m;
    for (const f of feedback) {
      if (!f.proto_node_id) continue;
      const entry = m.get(f.proto_node_id) ?? { total: 0, unhandled: 0 };
      entry.total += 1;
      if (!f.handled) entry.unhandled += 1;
      m.set(f.proto_node_id, entry);
    }
    return m;
  }, [feedback]);

  return (
    <FieldFeedbackContext.Provider value={map}>
      {children}
    </FieldFeedbackContext.Provider>
  );
}

/**
 * Returns counts for the given field id, or null when no feedback exists
 * (or when no provider is mounted — i.e. checklist instances).
 */
export function useFieldFeedback(fieldId: string): FieldFeedbackCounts | null {
  const map = useContext(FieldFeedbackContext);
  if (!map) return null;
  return map.get(fieldId) ?? null;
}
